import React, { useEffect, useState } from 'react';

import { guestSessionStorageKey } from '@aline/common';
import { link, params, routes } from '@aline/routes';
import { CircularProgress, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { CircleAlert } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { AlineButton } from 'src/components/generic/AlineButton';
import { captureExternalToolkitCallback } from 'src/external-toolkit-callback';
import type { ExternalToolkitCallback } from 'src/external-toolkit-callback';
import { makeStyles } from 'tss-react/mui';

/**
 * Message the connect window sends its opener once the provider has returned.
 *
 * A named constant on both sides because it is the whole contract: the opener
 * ignores anything else, and a typo here would look exactly like a user who
 * never finished authenticating.
 */
export const EXTERNAL_TOOLKIT_CONNECTED_MESSAGE = 'aline:external-toolkit-connected';

/** Long enough to be read, short enough not to feel stuck. */
const FALLBACK_REDIRECT_MS = 1_500;
const CONNECTION_PARAM = params.externalToolkitConnection;
const EMPTY_CALLBACK = {
  kind: 'fallback',
  shouldClearGuestSession: false,
} satisfies ExternalToolkitCallback;

const useStyles = makeStyles()(theme => ({
  failureAction: {
    marginTop: theme.spacing(3),
  },
  failureDescription: {
    marginTop: theme.spacing(1),
    maxWidth: 520,
  },
  failureIcon: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  failureTitle: {
    fontSize: 24,
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  successDescription: {
    marginTop: theme.spacing(2),
  },
  successTitle: {
    marginTop: theme.spacing(2),
  },
}));

const ConnectionFailureAction = React.memo(function ConnectionFailureAction({
  hasWaitingOpener,
}: {
  readonly hasWaitingOpener: boolean;
}): JSX.Element {
  const { classes } = useStyles();
  if (hasWaitingOpener)
    return (
      <AlineButton
        className={classes.failureAction}
        color="primary"
        variant="contained"
        onClick={closeWindow}
      >
        Close and try again
      </AlineButton>
    );

  return (
    <AlineButton
      className={classes.failureAction}
      color="primary"
      component={RouterLink}
      to={link.AdminIntegrations()}
      variant="contained"
    >
      Back to integrations
    </AlineButton>
  );
});

const ConnectionFailure = React.memo(function ConnectionFailure({
  description,
  hasWaitingOpener,
  title,
}: {
  readonly description: string;
  readonly hasWaitingOpener: boolean;
  readonly title: string;
}): JSX.Element {
  const { classes } = useStyles();
  return (
    <div className={classes.root}>
      <CircleAlert
        aria-hidden="true"
        className={classes.failureIcon}
        data-testid="connection-failure-icon"
        size={56}
        strokeWidth={1.75}
      />
      <Typography className={classes.failureTitle} variant="h1">
        {title}
      </Typography>
      <Typography className={classes.failureDescription} color="text.secondary" variant="body1">
        {description}
      </Typography>
      <ConnectionFailureAction hasWaitingOpener={hasWaitingOpener} />
    </div>
  );
});

const ConnectionSuccess = React.memo(function ConnectionSuccess({
  hasWaitingOpener,
}: {
  readonly hasWaitingOpener: boolean;
}): JSX.Element {
  const { classes } = useStyles();
  return (
    <div className={classes.root}>
      <CircularProgress size={24} />
      <Typography className={classes.successTitle} variant="subtitle1">
        Account connected
      </Typography>
      <Typography className={classes.successDescription} color="text.secondary" variant="body2">
        {hasWaitingOpener
          ? 'You can close this window — your conversation is already continuing.'
          : 'Taking you back to your integrations…'}
      </Typography>
    </div>
  );
});

const ConnectionResult = React.memo(function ConnectionResult({
  hasWaitingOpener,
  status,
}: {
  readonly hasWaitingOpener: boolean;
  readonly status: ReturnType<typeof getConnectionStatus>;
}): JSX.Element {
  switch (status) {
    case CONNECTION_PARAM.status.authenticationRequired:
      return (
        <ConnectionFailure
          description="Sign in to Aline, then try connecting the account again."
          hasWaitingOpener={hasWaitingOpener}
          title="Authentication required"
        />
      );
    case CONNECTION_PARAM.status.expired:
      return (
        <ConnectionFailure
          description="This connection attempt took too long. Start it again from Integrations."
          hasWaitingOpener={hasWaitingOpener}
          title="Connection attempt timed out"
        />
      );
    case CONNECTION_PARAM.status.error:
      return (
        <ConnectionFailure
          description="We could not connect the account. Try connecting again."
          hasWaitingOpener={hasWaitingOpener}
          title="Connection failed"
        />
      );
    case CONNECTION_PARAM.status.success:
      return <ConnectionSuccess hasWaitingOpener={hasWaitingOpener} />;
    default:
      return (
        <ConnectionFailure
          description="We could not confirm the account connection."
          hasWaitingOpener={hasWaitingOpener}
          title="Account not connected"
        />
      );
  }
});

/**
 * The page a provider redirects back to after consent.
 *
 * A successful callback with a waiting opener tells that window to re-check
 * its connection; a standalone callback tab returns to integrations. Failed
 * and incomplete callbacks stay put and offer an explicit retry path instead
 * of claiming success.
 */
export function ExternalToolkitConnectedPage({
  callback: initialCallback,
}: {
  readonly callback?: ExternalToolkitCallback;
}): JSX.Element {
  const [callback] = useState(
    () => initialCallback ?? captureExternalToolkitCallback() ?? EMPTY_CALLBACK
  );
  const [hasWaitingOpener] = useState(() => getHasOpener());
  const [handoffFailed, setHandoffFailed] = useState(false);
  const [status] = useState(() => getConnectionStatus(callback));

  useEffect(
    () =>
      startCallbackHandoff({
        callback,
        hasWaitingOpener,
        onGuestSessionClearFailure: () => setHandoffFailed(true),
        status,
      }),
    [callback, hasWaitingOpener, status]
  );

  return (
    <ConnectionResult hasWaitingOpener={hasWaitingOpener} status={handoffFailed ? null : status} />
  );
}

function getConnectionStatus(callback: ExternalToolkitCallback) {
  if (callback.kind === 'fallback') return null;
  if (callback.hasCompletionState || callback.hasSessionUri) return null;
  const { status } = callback;
  switch (status) {
    case CONNECTION_PARAM.status.authenticationRequired:
    case CONNECTION_PARAM.status.error:
    case CONNECTION_PARAM.status.expired:
      return status;
    case CONNECTION_PARAM.status.success:
      return getHasCompletionProof(callback.completionProof) ? status : null;
    default:
      return null;
  }
}

interface CallbackHandoffInput {
  readonly callback: ExternalToolkitCallback;
  readonly hasWaitingOpener: boolean;
  readonly onGuestSessionClearFailure: () => void;
  readonly status: ReturnType<typeof getConnectionStatus>;
}

interface RunningCallbackHandoff extends CallbackHandoffInput {
  readonly signal: AbortSignal;
}

function startCallbackHandoff(input: CallbackHandoffInput): () => void {
  const abort = new AbortController();
  void runCallbackHandoff({ ...input, signal: abort.signal });
  return () => abort.abort();
}

async function runCallbackHandoff({
  callback,
  hasWaitingOpener,
  onGuestSessionClearFailure,
  signal,
  status,
}: RunningCallbackHandoff): Promise<void> {
  if (callback.shouldClearGuestSession && !(await clearGuestSession())) {
    if (!signal.aborted) onGuestSessionClearFailure();
    return;
  }
  if (signal.aborted || !getIsSuccessfulCompletion(callback, status)) return;
  consumeCompletionProof(callback.completionProof);
  if (hasWaitingOpener) notifyOpener();
  else leaveOrphanedCallbackTab(signal);
}

function getIsSuccessfulCompletion(
  callback: ExternalToolkitCallback,
  status: ReturnType<typeof getConnectionStatus>
): callback is Extract<ExternalToolkitCallback, { kind: 'completion' }> {
  return callback.kind === 'completion' && status === CONNECTION_PARAM.status.success;
}

/**
 * No opener means this tab is not handing back to chat. Ask it to close; if
 * the browser refuses, send it back to Integrations.
 */
function leaveOrphanedCallbackTab(signal: AbortSignal): void {
  closeWindow();
  const redirectTimer = window.setTimeout(returnToIntegrations, FALLBACK_REDIRECT_MS);
  const cancelRedirect = () => clearTimeout(redirectTimer);
  if (signal.aborted) {
    cancelRedirect();
    return;
  }
  signal.addEventListener('abort', cancelRedirect, { once: true });
}

async function clearGuestSession(): Promise<boolean> {
  try {
    const auth = await import('src/auth');
    await auth.endGuestSession();
  } catch (error) {
    Sentry.captureException(error);
  }
  return clearLocalGuestSessionToken();
}

function clearLocalGuestSessionToken(): boolean {
  try {
    sessionStorage.removeItem(guestSessionStorageKey);
    return sessionStorage.getItem(guestSessionStorageKey) === null;
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
}

function returnToIntegrations(): void {
  window.location.replace(link.AdminIntegrations());
}

function getHasCompletionProof(proof: string | null): boolean {
  if (!proof) return false;
  const expectedCookie = `${getCompletionProofCookieName(proof)}=1`;
  return document.cookie.split(';').some(value => value.trim() === expectedCookie);
}

function consumeCompletionProof(proof: string | null): void {
  if (!proof) return;
  document.cookie = `${getCompletionProofCookieName(proof)}=; Max-Age=0; Path=${routes.ExternalToolkitConnected}; SameSite=Lax`;
}

function getCompletionProofCookieName(proof: string): string {
  return `${CONNECTION_PARAM.completionProof}.${proof}`;
}

function closeWindow(): void {
  window.close();
}

/**
 * Whether another Aline window is waiting on this one.
 *
 * Read through a guard rather than trusted directly: a cross-origin opener
 * throws on property access in some browsers, and an opener that has since
 * closed reports itself closed.
 */
function getHasOpener(): boolean {
  try {
    return Boolean(window.opener) && !window.opener.closed;
  } catch {
    return false;
  }
}

/**
 * Tell the opener the provider is done.
 *
 * Addressed to this app's own origin so the message cannot be read by whatever
 * else the user has open, and sent without any payload — the opener re-checks
 * the connection with the server rather than believing what a window told it.
 */
function notifyOpener(): void {
  try {
    window.opener?.postMessage(EXTERNAL_TOOLKIT_CONNECTED_MESSAGE, window.location.origin);
  } catch {
    /* The opener is gone or unreachable; its own poll still settles the turn. */
  }
}
